import { type ReactNode, useEffect, useRef, useState } from 'react'
import { AlertTriangle, Check, ChevronDown, ChevronRight, Circle, Clock, Copy, X } from 'lucide-react'

import type { VarlessCopyId } from '@npc/copy'
import type { Schemas } from '@npc/api-client'

import { t } from '../../copy'
import { T } from '../../copy/text'
import { runIsFinished, stepsForRun, type RunStep } from './runsteps'
import { BriefItemCard, isBriefItemCard } from './brief-card'
import { isRemoteAIInviteCard, RemoteAIInviteCard } from './remote-ai-card'

/**
 * 消息里那两张卡：**工作过程**与**结果总结**。
 *
 * 来自 arron 2026-08-30 的产品合同：「Lulu 要像 Codex 一样让用户看见
 * 真实工作过程，任务结束后给出清晰的结果总结，**但必须严格区分闲聊和任务，
 * 不能给所有消息套任务卡**」。
 *
 * # 这个文件为什么是独立的
 *
 * 另一条流（codex/left-sidebar-v1）正在改全局左侧导航，它对 `view.tsx`
 * 只有「移除重复会话栏」那一处最小接线。所以这一批渲染写在这儿，
 * **`view.tsx` 只加一行 `<RunBlocks>`**——集成时那一行挪到哪儿都行，
 * 而它离 `lulu-rail` 那块（323/350/365 行）有五百多行远，不碰。
 *
 * # 三条贯穿的规矩
 *
 *  1. **没有事实就不画。** 这里没有任何一处会在数据缺席时补一个默认值：
 *     没有 steps 就不画步骤，没有 checks 就说「未验证」，
 *     算不出时长就不画那一行。**不造百分比、不造进度条。**
 *  2. **空字段不渲染。** 每一段都先判「有没有」，没有就整段不出——
 *     一个空标题下面什么都没有，比不出这一段糟。
 *  3. **认不出的块整块跳过。** 渲染合同（blocks.go 顶部）：
 *     客户端认不出某个 type 就跳过它、显示 body。所以这里对未知 type
 *     一律回 null，而 body 由 view.tsx 那侧照常渲染。
 *
 * # 2026-08-30 的视觉返工，以及它的根因
 *
 * arron 看完第一版截图：「视觉仍像调试报表」。**根因不是排版取舍，
 * 是六个 token 类名压根不存在**——`npc-lulu-line`、`npc-lulu-surface2`、
 * `npc-lulu-accent`、`npc-ok`、`npc-danger`、`npc-warn`、`rounded-npc-md`。
 * 真名是 `npc-lulu-line-1/2/3`、`npc-lulu-bg-1..4`、`npc-lulu-ok/warn/bad`、
 * `rounded-npc-card-md`。
 *
 * Tailwind 认不出一个类名时**什么都不生成，也不报错**：于是
 * `border` 单独生效、边框色回退成 currentColor（近黑），背景是透明的，
 * 四个状态色全变成同一个正文黑。屏幕上那圈硬黑边就是这么来的。
 *
 * **这一类错误没有任何东西会红**——编译过、用例过、`check-tokens` 只查
 * 硬编码的十六进制，不查「你引用的 token 存不存在」。所以补了一条
 * `token-classes.test.ts`：扫这一屏用到的每个 `-npc-` 类名，
 * 在 `packages/tokens` 里查得到才算数。
 */

import type { PendingDecision } from './queries'

type Block = Schemas['MessageBlock']
type RunStatus = Schemas['RunStatus']
type ReceiptSource = Schemas['ConversationReceipt']['source']
type ReceiptWithPlatformTrust = Schemas['ConversationReceipt'] & {
  readonly trusted_platform_action?: boolean
}

/**
 * The backend trust bit is additive, so older API clients and stored responses may not have it.
 * Missing is intentionally false: source alone is caller-controlled on AgentBridge messages.
 */
export function trustedPlatformActionOf(
  receipt: Schemas['ConversationReceipt'] | null | undefined,
): boolean {
  return (receipt as ReceiptWithPlatformTrust | null | undefined)?.trusted_platform_action === true
}

/** 一步的真实状态。与服务端 blocks_run.go 的 runStepStates 同一份词表。 */
type StepState = 'running' | 'succeeded' | 'failed' | 'waiting' | 'skipped'

interface ProcessStep {
  readonly at: string
  readonly actor: string
  readonly event: string
  readonly summary: string
  readonly state: StepState
  readonly object_id?: string
}

/**
 * 运行中最多显示几步。
 *
 * 合同第三节：「运行中默认展开，只放 **3–6 条**最有信息量的当前/最近步骤」。
 * 取上界 6——少于 6 步时全显示，多了取**最近**的六条：
 * 正在发生的事比十分钟前那条要紧。
 */
const LIVE_STEPS = 6

function isProcessCard(b: Block): boolean {
  return b.type === 'process_card'
}

function isTaskDecisionCard(b: Block): boolean {
  return b.type === 'task_decision_card'
}

/**
 * 红线卡进消息（修复任务闭环 §A）。
 *
 * 那句「这一步碰到钱了，得你点一下」原来只是一句话，按钮在 Sidecar 里。
 * 现在同意 / 拒绝 / 问 Lulu 就在这句话下面。**授权只认点击**：会话里聊什么
 * 都不算，按钮走的是 /decisions/{id}/answer（老板权限 + 二次验证）。
 *
 * 决定 id 按 task_id 在待决定里找：卡入队那一刻 receipt 还不一定有。
 * 找不到 = 处理过了（或者过期了），说一句，不画一颗按不动的按钮。
 */
function TaskDecisionCard({
  block,
  decisions,
  onAnswer,
  onAsk,
}: {
  block: Block
  decisions: readonly PendingDecision[] | null | undefined
  onAnswer?: (decisionId: string, approve: boolean) => Promise<void>
  onAsk?: (text: string) => void
}) {
  const raw = block as unknown as Record<string, unknown>
  const taskId = str(raw, 'task_id') ?? ''
  const what = str(raw, 'what')
  const pending = decisions?.find((d) => d.task_id === taskId)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const answer = (approve: boolean) => {
    if (!pending || !onAnswer) return
    setBusy(true)
    setFailed(false)
    void onAnswer(pending.id, approve)
      .catch(() => setFailed(true))
      .finally(() => setBusy(false))
  }
  return (
    <div
      data-testid="lulu-task-decision-card"
      data-task-id={taskId}
      data-state={pending ? 'pending' : decisions === undefined ? 'loading' : 'settled'}
      className="mt-2 rounded-npc-card border border-npc-red-border bg-npc-red-bg px-3 py-2"
    >
      {what ? <p className="text-npc-caption text-npc-red-ink">{what}</p> : null}
      {pending ? (
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="lulu-task-decision-approve"
            disabled={busy || onAnswer === undefined}
            onClick={() => answer(true)}
            className="rounded-npc-chip bg-npc-green px-3 py-1 text-npc-caption text-npc-panel disabled:opacity-60"
          >
            <T id="lulu_workbench.red.card_approve" />
          </button>
          <button
            type="button"
            data-testid="lulu-task-decision-reject"
            disabled={busy || onAnswer === undefined}
            onClick={() => answer(false)}
            className="rounded-npc-chip border border-npc-lulu-line-1 px-3 py-1 text-npc-caption text-npc-lulu-t2 disabled:opacity-60"
          >
            <T id="lulu_workbench.red.card_reject" />
          </button>
          {onAsk ? (
            <button
              type="button"
              data-testid="lulu-task-decision-ask"
              onClick={() => onAsk(t('lulu_workbench.red.card_ask_seed'))}
              className="rounded-npc-chip border border-npc-lulu-line-1 px-3 py-1 text-npc-caption text-npc-lulu-t2"
            >
              <T id="lulu_workbench.red.card_ask" />
            </button>
          ) : null}
        </div>
      ) : decisions === undefined ? null : (
        <p data-testid="lulu-task-decision-settled" className="mt-1 text-npc-meta text-npc-lulu-t3">
          <T id="lulu_workbench.red.card_settled" />
        </p>
      )}
      {failed ? (
        <p data-testid="lulu-task-decision-failed" className="mt-1 text-npc-meta text-npc-red-ink">
          <T id="lulu_workbench.red.card_failed" />
        </p>
      ) : null}
    </div>
  )
}

function isResultCard(b: Block): boolean {
  return b.type === 'result_card'
}

/** 从块里取一个字符串字段。**不认得的形状回 undefined**，调用方据此不渲染。 */
function str(b: Record<string, unknown>, key: string): string | undefined {
  const v = b[key]
  return typeof v === 'string' && v.trim() !== '' ? v : undefined
}

function num(b: Record<string, unknown>, key: string): number | undefined {
  const v = b[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

function arr(b: Record<string, unknown>, key: string): unknown[] {
  const v = b[key]
  return Array.isArray(v) ? v : []
}

/**
 * 把毫秒说成人话。
 *
 * **算不出来就回 undefined**，调用方整行不渲染——
 * 填一个「0 秒」是在说一件没发生的事。
 */
function humanDuration(ms: number | undefined): string | undefined {
  if (ms === undefined || ms < 0) return undefined
  const seconds = Math.round(ms / 1000)
  if (seconds < 60) return t('lulu_workbench.run.duration.seconds', { n: String(seconds) })
  return t('lulu_workbench.run.duration.minutes', { n: String(Math.round(seconds / 60)) })
}

/* ------------------------------------------------------------------ *
 * 视觉基座
 *
 * **每个类名都在 packages/tokens 里查得到**（token-classes.test.ts 盯着）。
 * 第一版栽的正是这一条：写了六个不存在的名字，Tailwind 一声不响地
 * 什么都不生成，边框回退成近黑、状态色全变成正文黑。
 * ------------------------------------------------------------------ */

/**
 * 卡片外壳。
 *
 * `max-w-[680px]` 是**桌面上的阅读宽度**：聊天列在 1440 下有 900 多像素，
 * 一张摊满的卡会让每行走到七八十个字，扫不动。手机上 680 大于视口，
 * `w-full` 让它退回占满——不需要第二套断点。
 */
const CARD =
  'mt-2 w-full max-w-[680px] overflow-hidden rounded-npc-card-md ' +
  'border border-npc-lulu-line-1 bg-npc-lulu-bg-1'

/** 段与段之间只用一条发丝线，不再各自套一个框。 */
const DIVIDER = 'border-t border-npc-lulu-line-1'

const STEP_TONE: Record<StepState, string> = {
  running: 'bg-npc-lulu-t3',
  succeeded: 'bg-npc-lulu-ok',
  failed: 'bg-npc-lulu-bad',
  waiting: 'bg-npc-lulu-warn',
  skipped: 'bg-npc-lulu-line-3',
}

/**
 * StepRow 是过程里的一行。
 *
 * # 显示什么、不显示什么
 *
 * 显示：状态点、**谁在做**、那一步的人话摘要。
 * 不显示：事件类型（`run.tool_started` 之类）、object_id、时间戳。
 * arron 2026-08-30 逐字：「不堆原始内部字段」。那几样进 `title`，
 * 需要追溯的人把鼠标停上去就有，扫这一列的人不会被它们挡住。
 *
 * **actor 单独显示**是合同第三节的落点：多 Agent 要看得出是谁在做，
 * 不能把所有动作都写成 Lulu。
 */
function StepRow({ step, last }: { step: ProcessStep; last: boolean }) {
  return (
    <li
      data-testid="lulu-run-step"
      data-state={step.state}
      title={step.object_id !== undefined ? `${step.event} · ${step.object_id}` : step.event}
      className="relative flex gap-2.5 pb-2 last:pb-0"
    >
      {/* 左侧那条竖线把几步串成一条时间线。最后一步不画，
          否则线会从末尾那个点继续往下拖出一截。 */}
      {last ? null : (
        <span
          aria-hidden
          className="absolute left-[3px] top-[10px] bottom-0 w-px bg-npc-lulu-line-1"
        />
      )}
      <span
        aria-hidden
        className={`relative mt-[5px] size-[7px] shrink-0 rounded-npc-full ${
          STEP_TONE[step.state] ?? 'bg-npc-lulu-line-3'
        } ${step.state === 'running' ? 'animate-npc-pulse' : ''}`}
      />
      <span className="min-w-0 flex-1 text-npc-meta leading-relaxed">
        <span data-testid="lulu-run-step-actor" className="text-npc-lulu-t3">
          {step.actor}
        </span>
        <span className="mx-1.5 text-npc-lulu-line-3">·</span>
        <span className="break-words text-npc-lulu-t1">{step.summary}</span>
      </span>
    </li>
  )
}

/**
 * ProcessCard 是那条内联的「工作过程」。
 *
 * # 四态里的第四个
 *
 * 合同要「完成 / 部分成功 / 失败 / 需用户」四态都清楚。前三个是收口之后
 * 的事，落在结果卡上；**「需用户」是运行中的事**，只可能出现在这儿。
 * 判据是最近一步的 state 是 `waiting`——那是服务端事件上的值，
 * 不是这一侧猜的。
 *
 * # 展开还是折叠
 *
 * 运行中默认展开；收口那一刻自动折成一行「查看过程 · N 步 · X 分钟」。
 * 判据是有没有 `step_count`——服务端只在收口时才写它，
 * 所以它非空本身就是那个信号，不需要第二个标志（第二个会与它分家）。
 */
export function ProcessCard({
  block,
  liveSteps,
}: {
  block: Block
  liveSteps?: readonly RunStep[]
}) {
  const raw = block as unknown as Record<string, unknown>
  const runId = str(raw, 'run_id')
  const stepCount = num(raw, 'step_count')

  // **实时步骤盖住静态那份。**
  //
  // block 里那份是服务端收口时写下的摘要，实时那份是这一刻从 SSE 收到的。
  // 运行中只有后者，收口后两者都在——而以实时那份为准是对的：
  // 它更新，而且它就是那份摘要的来源。
  //
  // **都没有就什么都不画**：不补一个「正在处理」。
  const live = stepsForRun(liveSteps ?? [], runId)
  const fromBlock = arr(raw, 'steps')
    .filter((s): s is ProcessStep => typeof s === 'object' && s !== null)
    .filter((s) => typeof s.actor === 'string' && typeof s.summary === 'string')
  const steps: ProcessStep[] =
    live.length > 0
      ? live.map((s) => ({
          at: s.at, actor: s.actor, event: s.event,
          summary: s.summary, state: s.state, object_id: s.objectId,
        }))
      : fromBlock

  // 收口判据**优先看实时事件**（收到过 run.finished），
  // 其次才是 block 上那个 step_count。前者是当下的事实，后者是历史的快照。
  const finished = live.length > 0 ? runIsFinished(live) : stepCount !== undefined

  // **收口那一刻自动折叠**，而且只折那一次：`userOpened` 记住他自己点开过，
  // 那之后不再自动折——否则他刚点开就被合上，比不折还糟。
  const [open, setOpen] = useState(!finished)
  const userOpened = useRef(false)
  const wasFinished = useRef(finished)
  useEffect(() => {
    if (finished && !wasFinished.current && !userOpened.current) setOpen(false)
    wasFinished.current = finished
  }, [finished])

  const duration = humanDuration(num(raw, 'duration_ms'))
  // 折叠那一行要**步数与时长都有**才画：只有其中一个的时候那句话说不完整，
  // 而补一个假的正是这一整套要避免的东西。
  // 步数优先用实时那份的条数（这一刻的事实），stepCount 是收口时的快照。
  //
  // 「undefined 步」那种渲染到不了：live 非空时 liveCount 一定有值；
  // live 为空时 finished 就是 `stepCount !== undefined`，缺席时整条分支走不到。
  // 曾经为它加过一道守卫，变异验证发现**永远不红**——一条到不了的守卫
  // 会让下一个人以为这一格有人看着，所以删了，理由留在这儿。
  const liveCount = live.length > 0 ? live.length : undefined
  const shownCount = liveCount ?? stepCount

  // **需用户**：最近一步在等。它排在「正在做」前面判——
  // 一轮在等人的活也是「在跑」，而那两句话要他做的事完全不同。
  const latest = steps.length > 0 ? steps[steps.length - 1] : undefined
  const needsYou = !finished && latest?.state === 'waiting'

  const collapsedLabel =
    finished && duration !== undefined
      ? t('lulu_workbench.run.process.collapsed', { steps: String(shownCount), duration })
      : t('lulu_workbench.run.process.title')

  const Chevron = open ? ChevronDown : ChevronRight
  return (
    <div
      data-testid="lulu-process-card"
      data-run-id={runId}
      data-run-kind={str(raw, 'run_kind')}
      data-finished={finished ? 'true' : 'false'}
      data-live-state={finished ? 'finished' : needsYou ? 'needs_you' : 'running'}
      className={CARD}
    >
      <button
        type="button"
        data-testid="lulu-process-toggle"
        aria-expanded={open}
        onClick={() => {
          userOpened.current = true
          setOpen((v) => !v)
        }}
        className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-npc-meta text-npc-lulu-t3 transition-colors hover:bg-npc-lulu-bg-3"
      >
        <Chevron aria-hidden className="size-3.5 shrink-0 text-npc-lulu-t4" />
        <span className="min-w-0 flex-1 truncate">{collapsedLabel}</span>
        {finished ? null : needsYou ? (
          <span
            data-testid="lulu-process-needs-you"
            className="flex shrink-0 items-center gap-1.5 rounded-npc-chip bg-npc-lulu-bg-3 px-2 py-0.5 text-npc-lulu-warn"
          >
            <Clock aria-hidden className="size-3" />
            <T id="lulu_workbench.run.process.needs_you" />
          </span>
        ) : (
          <span
            data-testid="lulu-process-live"
            className="flex shrink-0 items-center gap-1.5 text-npc-lulu-t4"
          >
            <span aria-hidden className="size-[6px] animate-npc-pulse rounded-npc-full bg-npc-lulu-t3" />
            <T id="lulu_workbench.run.process.running" />
          </span>
        )}
      </button>
      {open && steps.length > 0 ? (
        <ol className={`${DIVIDER} flex flex-col px-3.5 py-2.5`}>
          {(finished ? steps : steps.slice(-LIVE_STEPS)).map((step, i, all) => (
            <StepRow key={`${step.at}-${i}`} step={step} last={i === all.length - 1} />
          ))}
        </ol>
      ) : null}
    </div>
  )
}

const STATUS_COPY = {
  completed: 'lulu_workbench.run.status.completed',
  partial: 'lulu_workbench.run.status.partial',
  failed: 'lulu_workbench.run.status.failed',
  cancelled: 'lulu_workbench.run.status.cancelled',
} as const satisfies Record<RunStatus, VarlessCopyId>

/**
 * 状态那颗小药丸的配色。
 *
 * **四档四个样子**，而且底色也换——只换文字颜色的话，
 * 「已完成」与「未完成」在扫一眼的距离上是同一个形状。
 */
const STATUS_TONE: Record<RunStatus, string> = {
  completed: 'bg-npc-lulu-bg-3 text-npc-lulu-ok',
  partial: 'bg-npc-lulu-bg-3 text-npc-lulu-warn',
  failed: 'bg-npc-lulu-bg-3 text-npc-lulu-bad',
  cancelled: 'bg-npc-lulu-bg-3 text-npc-lulu-t3',
}

const STATUS_ICON: Record<RunStatus, typeof Check> = {
  completed: Check,
  partial: AlertTriangle,
  failed: X,
  cancelled: Circle,
}

const CHECK_COPY = {
  passed: 'lulu_workbench.run.check.passed',
  failed: 'lulu_workbench.run.check.failed',
  skipped: 'lulu_workbench.run.check.skipped',
} as const satisfies Record<string, VarlessCopyId>

const CHECK_TONE: Record<string, string> = {
  passed: 'text-npc-lulu-ok',
  failed: 'text-npc-lulu-bad',
  skipped: 'text-npc-lulu-t4',
}

/**
 * 发布那三档的小标题。
 *
 * **写成一张表而不是拼字符串**：拼出来的是模板字面量类型，
 * 而 `T` 要的是真实存在的文案 id——拼错一个字母时字符串仍然「像」一个 id，
 * 而它渲染不出来。类型系统在这儿做的正是「文案必须走 copy contract」。
 */
const RELEASE_COPY = {
  health: 'lulu_workbench.run.release.health',
  smoke: 'lulu_workbench.run.release.smoke',
  rollback: 'lulu_workbench.run.release.rollback',
} as const satisfies Record<string, VarlessCopyId>

/** Section 是结果卡里的一段。**空的整段不渲染**——这是合同第五节。 */
function Section({
  id,
  children,
  testid,
}: {
  id: VarlessCopyId
  children: ReactNode
  testid?: string
}) {
  return (
    <div data-testid={testid} className={`${DIVIDER} px-3.5 py-2.5`}>
      <div className="text-npc-meta-sm text-npc-lulu-t4">
        <T id={id} />
      </div>
      <div className="mt-1.5">{children}</div>
    </div>
  )
}

function Bullets({ items, testid }: { items: string[]; testid: string }) {
  return (
    <ul data-testid={testid} className="flex flex-col gap-1 text-npc-body-sm text-npc-lulu-t1">
      {items.map((item, i) => (
        <li key={i} className="flex gap-2">
          <span aria-hidden className="mt-[7px] size-[3px] shrink-0 rounded-npc-full bg-npc-lulu-line-3" />
          <span className="min-w-0 break-words">{item}</span>
        </li>
      ))}
    </ul>
  )
}

/**
 * ShaChip 是一枚**短显 + 可复制**的 SHA。
 *
 * 合同第三条逐字：「SHA 短显并可复制」。短显是给扫的人看的，
 * 完整值给要用它的人——两个需求都真，而把完整的 40 位摊在卡上
 * 会在手机那一档把一行撑成两行半。
 *
 * **复制失败不假装成功。** `navigator.clipboard` 在非安全上下文里会抛，
 * 那时按钮回到「复制」——一句假的「复制好了」会让他去粘贴一段空白。
 */
function ShaChip({ id, label, sha }: { id: string; label: string; sha: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <span data-testid={id} className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="text-npc-body-sm text-npc-lulu-t1">{label}</span>
      <button
        type="button"
        data-testid={`${id}-copy`}
        title={sha}
        onClick={() => {
          void (async () => {
            try {
              await navigator.clipboard.writeText(sha)
              setCopied(true)
              window.setTimeout(() => setCopied(false), 1600)
            } catch {
              setCopied(false)
            }
          })()
        }}
        className="flex items-center gap-1.5 rounded-npc-tag-sm bg-npc-lulu-bg-3 px-1.5 py-0.5 font-npc-mono text-npc-meta-sm text-npc-lulu-t2 transition-colors hover:bg-npc-lulu-bg-4"
      >
        <span>{sha.slice(0, 7)}</span>
        <Copy aria-hidden className="size-3 text-npc-lulu-t4" />
        <span className="sr-only">
          <T id={copied ? 'lulu_workbench.run.sha.copied' : 'lulu_workbench.run.sha.copy'} />
        </span>
      </button>
      {copied ? (
        <span data-testid={`${id}-copied`} className="text-npc-meta-sm text-npc-lulu-ok">
          <T id="lulu_workbench.run.sha.copied" />
        </span>
      ) : null}
    </span>
  )
}

/**
 * ResultCard 是任务收口后的结构化总结。
 *
 * # 默认露什么
 *
 * arron 2026-08-30 逐字：「默认只露出：状态、交付结论、关键变化、验证、
 * 上线状态。文件路径、完整 SHA、所有步骤移到『查看详情』」。
 *
 * 所以默认那一屏是五段，**其余全在折叠里**：产物引用（带路径）、
 * 改了哪些文件、还有这些没解决、接下来。
 *
 * # 但没解决的不能悄悄收起来
 *
 * 折叠可以，**在折叠那一行上要说清里面有几件要紧的**——
 * 「查看详情 · 2 项没解决」。收起来是版面决定，藏起来是另一回事。
 *
 * # 「未验证」不是兜底，是结论
 *
 * 没有 checks 就显示「未验证」。合同：「只列实际执行的测试/检查及结果，
 * **未运行必须写『未验证』，禁止推断**」。
 */
export function ResultCard({
  block,
  messageBody,
  receiptSource,
  trustedPlatformAction,
  runKind,
}: {
  block: Block
  /**
   * 这条消息的正文。
   *
   * **用来避免同一句话说两遍**：`resultBlocksFor` 拿那句回话当 headline，
   * 而 view.tsx 同时渲染 body 与这张卡，于是屏幕上同一句话出现两次。
   * 判据是**逐字相同**（去掉两头空白之后），不是「差不多」。
   */
  messageBody?: string
  /**
   * 这条消息的服务端回执来源。
   *
   * AgentBridge 允许远程 AI 自报 blocks 和 platform_action，所以两者本身都不是
   * 平台事实。生产事实必须同时满足 source=platform_action 与服务端可信位为 true。
   */
  receiptSource?: ReceiptSource
  /** 服务端写入的内部 settlement 来源证明。旧响应缺失时必须按 false 处理。 */
  trustedPlatformAction?: boolean
  /**
   * 这一轮是哪一档（chat / tool / development），来自同一条消息里那张过程卡。
   *
   * 「验证」那一格要靠它分两句：一轮**没有东西可验**（聊天、只查了一次项目）
   * 与一轮**该验没验**（开发活没跑检查）以前都写「未验证」，而看完之后要做的
   * 下一个动作不一样——前者什么都不用做，后者要去催。2026-09-04 arron 看到
   * 一张被叫停的闲聊卡上「验证 · 未验证」，问「是不是有验证没做」。
   */
  runKind?: string
}) {
  const raw = block as unknown as Record<string, unknown>
  const status = str(raw, 'status') as RunStatus | undefined
  const headline = str(raw, 'headline')
  const [detailsOpen, setDetailsOpen] = useState(false)
  if (status === undefined || headline === undefined || STATUS_COPY[status] === undefined) {
    // 状态或结论缺一个就整张不渲染。**一张没有结论的结果卡比没有卡糟**：
    // 它占着「这件事有交代了」的位置，而里面什么都没有。
    return null
  }

  const did = arr(raw, 'did').filter((v): v is string => typeof v === 'string')
  const risks = arr(raw, 'risks').filter((v): v is string => typeof v === 'string')
  const next = arr(raw, 'next').filter((v): v is string => typeof v === 'string')
  const artifacts = arr(raw, 'artifacts').filter(
    (v): v is Record<string, unknown> => typeof v === 'object' && v !== null,
  )
  const checks = arr(raw, 'checks').filter(
    (v): v is Record<string, unknown> => typeof v === 'object' && v !== null,
  )
  const changes = arr(raw, 'changes').filter(
    (v): v is Record<string, unknown> => typeof v === 'object' && v !== null,
  )
  const release =
    typeof raw.release === 'object' && raw.release !== null
      ? (raw.release as Record<string, unknown>)
      : undefined
  // release 里的每一项都是平台生产事实，不只是 production_verified 徽标。
  // 远程 AI 自述、平台配置文案或缺席回执都不能据此画出已合并、已上线、
  // health/smoke/rollback 通过等确定性断言。普通交付物与代码变化不受影响。
  const trustedRelease =
    receiptSource === 'platform_action' && trustedPlatformAction === true ? release : undefined
  const productionVerified = trustedRelease?.production_verified === true
  // production_state 在这之前**前端从来没读过**——后端写了三档，
  // 屏幕上只认 production_verified 那一档，于是 rolled_back 与 pending
  // 都是不可见的。这里先把最危险的那一档接上（另一档见下面的登记）。
  const rolledBack =
    trustedRelease !== undefined && str(trustedRelease, 'production_state') === 'rolled_back'
  // 第三档：交出去了、线上还没验完。原来这一档什么都不画，而在这张卡上
  // 「不画」读起来就是「没上线」——把等着验的说成没交，是另一句谎话。
  const productionPending =
    trustedRelease !== undefined && str(trustedRelease, 'production_state') === 'pending'

  const hasDetails = artifacts.length + changes.length + risks.length + next.length > 0
  const StatusIcon = STATUS_ICON[status]
  const Chevron = detailsOpen ? ChevronDown : ChevronRight

  return (
    <div data-testid="lulu-result-card" data-run-id={str(raw, 'run_id')} data-status={status} className={CARD}>
      {/* ---- 状态 + 交付结论：一屏里最先被读到的两样 ---- */}
      <div className="px-3.5 py-3">
        <span
          data-testid="lulu-result-status"
          className={`inline-flex items-center gap-1.5 rounded-npc-chip px-2 py-0.5 text-npc-meta ${STATUS_TONE[status]}`}
        >
          <StatusIcon aria-hidden className="size-3.5" />
          <T id={STATUS_COPY[status]} />
        </span>
        {headline === (messageBody ?? '').trim() ? null : (
          <p
            data-testid="lulu-result-headline"
            className="mt-2 break-words text-npc-body-sm text-npc-lulu-t1"
          >
            {headline}
          </p>
        )}
      </div>

      {/* ---- 关键变化 ---- */}
      {did.length > 0 ? (
        <Section id="lulu_workbench.run.section.did">
          <Bullets items={did} testid="lulu-result-did" />
        </Section>
      ) : null}

      {/* ---- 验证。**这一段永远渲染**：没有 checks 就说「未验证」，
              那不是兜底，是结论——服务端没给就是没跑过。 ---- */}
      <Section id="lulu_workbench.run.section.checks">
        {checks.length > 0 ? (
          <ul data-testid="lulu-result-checks" className="flex flex-wrap gap-x-4 gap-y-1.5">
            {checks.map((c, i) => {
              const name = str(c, 'name')
              const result = str(c, 'result') as keyof typeof CHECK_COPY | undefined
              if (name === undefined || result === undefined || CHECK_COPY[result] === undefined) {
                return null
              }
              return (
                <li
                  key={i}
                  data-testid="lulu-result-check"
                  data-result={result}
                  className="flex items-center gap-1.5 text-npc-body-sm"
                >
                  <span className="break-words text-npc-lulu-t1">{name}</span>
                  <span className={`text-npc-meta ${CHECK_TONE[result] ?? 'text-npc-lulu-t3'}`}>
                    <T id={CHECK_COPY[result]} />
                  </span>
                </li>
              )
            })}
          </ul>
        ) : runKind === 'chat' || runKind === 'tool' ? (
          <span
            data-testid="lulu-result-nothing-to-verify"
            className="text-npc-body-sm text-npc-lulu-t3"
          >
            <T id="lulu_workbench.run.nothing_to_verify" />
          </span>
        ) : (
          <span data-testid="lulu-result-unverified" className="text-npc-body-sm text-npc-lulu-t3">
            <T id="lulu_workbench.run.unverified" />
          </span>
        )}
      </Section>

      {/* ---- 上线状态。**合并与上线两行分开**：合同逐字，
              不能用「已提交」冒充「已上线」。 ---- */}
      {trustedRelease !== undefined ? (
        <Section id="lulu_workbench.run.section.release" testid="lulu-result-release">
          <div className="flex flex-col gap-1.5">
            {/* # 回滚这一句**排在「生产已验证」前面，而且互斥**

                一次先上线、后来又被回滚的发布，两句在时间上都曾经为真，
                而屏幕上只能说一句——那一句必须是「线上现在跑的不是它」。
                这一格回答的是**现在跑的是哪一版**，不是曾经跑过哪一版。

                服务端那侧同一个顺序陷阱一共有六处（public_phase、
                lineagePhaseImmutable、delivery_projection、证据名次、
                user_state 的两份实现、以及唤醒 watch 那处），
                每一处单看都像「本来就该拦住不足证据」的守卫——
                而回滚正好长得像不足证据。这里是那条链的最后一节。

                回滚发生时服务端会把 deploy_sha 与 production_verified 一起撤掉，
                所以正常情况下 productionVerified 已经是 false；
                这里仍然写成互斥，是因为**不能靠上游一定撤干净**
                （少撤一次的表现就是屏幕上同时挂着「已上线」和「退回去了」）。 */}
            {rolledBack ? (
              <span
                data-testid="lulu-result-rolled-back"
                role="status"
                className="inline-flex w-fit items-center gap-1.5 rounded-npc-chip border border-npc-lulu-warn bg-npc-lulu-bg-3 px-2 py-0.5 text-npc-meta text-npc-lulu-t1"
              >
                <T id="lulu_workbench.run.release.rolled_back" />
              </span>
            ) : productionVerified ? (
              <span
                data-testid="lulu-result-production-verified"
                role="status"
                className="inline-flex w-fit items-center gap-1.5 rounded-npc-chip border border-npc-lulu-ok bg-npc-lulu-bg-3 px-2 py-0.5 text-npc-meta text-npc-lulu-t1"
              >
                <Check aria-hidden className="size-3.5 text-npc-lulu-ok" />
                <T id="lulu_workbench.run.release.production_verified" />
              </span>
            ) : productionPending ? (
              <span
                data-testid="lulu-result-production-pending"
                role="status"
                className="inline-flex w-fit items-center gap-1.5 rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-3 px-2 py-0.5 text-npc-meta text-npc-lulu-t2"
              >
                <T id="lulu_workbench.run.release.pending" />
              </span>
            ) : null}
            {rolledBack && str(trustedRelease, 'rollback_sha') !== undefined ? (
              <ShaChip
                id="lulu-result-rolled-back-sha"
                label={t('lulu_workbench.run.release.rolled_back_label')}
                sha={str(trustedRelease, 'rollback_sha')!}
              />
            ) : null}
            {str(trustedRelease, 'merge_sha') !== undefined ? (
              <ShaChip
                id="lulu-result-merged"
                label={t('lulu_workbench.run.release.merged_label')}
                sha={str(trustedRelease, 'merge_sha')!}
              />
            ) : null}
            {str(trustedRelease, 'deploy_sha') !== undefined ? (
              <ShaChip
                id="lulu-result-deployed"
                label={t('lulu_workbench.run.release.deployed_label')}
                sha={str(trustedRelease, 'deploy_sha')!}
              />
            ) : null}
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              {(['health', 'smoke', 'rollback'] as const).map((key) => {
                const v = str(trustedRelease, key) as keyof typeof CHECK_COPY | undefined
                if (v === undefined || CHECK_COPY[v] === undefined) return null
                return (
                  <span
                    key={key}
                    data-testid={`lulu-result-${key}`}
                    className="flex items-center gap-1.5 text-npc-body-sm text-npc-lulu-t1"
                  >
                    <T id={RELEASE_COPY[key]} />
                    <span className={`text-npc-meta ${CHECK_TONE[v] ?? 'text-npc-lulu-t3'}`}>
                      <T id={CHECK_COPY[v]} />
                    </span>
                  </span>
                )
              })}
            </div>
          </div>
        </Section>
      ) : null}

      {/* ---- 其余全在这后面 ---- */}
      {hasDetails ? (
        <div className={DIVIDER}>
          <button
            type="button"
            data-testid="lulu-result-details-toggle"
            aria-expanded={detailsOpen}
            onClick={() => setDetailsOpen((v) => !v)}
            className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-npc-meta text-npc-lulu-t3 transition-colors hover:bg-npc-lulu-bg-3"
          >
            <Chevron aria-hidden className="size-3.5 shrink-0 text-npc-lulu-t4" />
            <T id="lulu_workbench.run.details.open" />
            {/* **没解决的不许悄悄收进去**：收起来的那一行上要说清有几件。 */}
            {risks.length > 0 ? (
              <span data-testid="lulu-result-unresolved-hint" className="text-npc-lulu-warn">
                {t('lulu_workbench.run.details.unresolved', { n: String(risks.length) })}
              </span>
            ) : null}
          </button>
          {detailsOpen ? (
            <div data-testid="lulu-result-details">
              {artifacts.length > 0 ? (
                <Section id="lulu_workbench.run.section.artifacts">
                  <ul data-testid="lulu-result-artifacts" className="flex flex-col gap-1.5">
                    {artifacts.map((a, i) => {
                      const label = str(a, 'label')
                      const href = str(a, 'href')
                      const ref = str(a, 'ref')
                      if (label === undefined) return null
                      return (
                        <li key={i} className="min-w-0 text-npc-body-sm">
                          {href !== undefined ? (
                            <a href={href} className="break-words text-npc-lulu-ok underline">
                              {label}
                            </a>
                          ) : (
                            <span className="break-words text-npc-lulu-t1">{label}</span>
                          )}
                          {/* 完整值保留：这里是「查看详情」，摊开就是要给全的。
                              `break-all` 让长路径在 390 宽下换行而不是横滚。 */}
                          {ref !== undefined ? (
                            <span className="mt-0.5 block break-all font-npc-mono text-npc-meta-sm text-npc-lulu-t4">
                              {ref}
                            </span>
                          ) : null}
                        </li>
                      )
                    })}
                  </ul>
                </Section>
              ) : null}

              {risks.length > 0 ? (
                <Section id="lulu_workbench.run.section.risks">
                  <Bullets items={risks} testid="lulu-result-risks" />
                </Section>
              ) : null}

              {next.length > 0 ? (
                <Section id="lulu_workbench.run.section.next">
                  <Bullets items={next} testid="lulu-result-next" />
                </Section>
              ) : null}

              {/* 改了哪些文件用纵向列表不用表格：一张表在 390 宽下必横滚。 */}
              {changes.length > 0 ? (
                <Section id="lulu_workbench.run.section.changes">
                  <ul data-testid="lulu-result-changes" className="flex flex-col gap-1">
                    {changes.map((c, i) => {
                      const path = str(c, 'path')
                      if (path === undefined) return null
                      return (
                        <li
                          key={i}
                          className="break-all font-npc-mono text-npc-meta-sm text-npc-lulu-t2"
                        >
                          {path}
                        </li>
                      )
                    })}
                  </ul>
                </Section>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/**
 * StreamUnknown 是 SSE 断了那一刻的样子。
 *
 * 合同第三节：「SSE 断线**立即**显示未知/重连」。
 * **不留着最后那个状态**——那是在用一个过期的事实冒充当下的事实，
 * 而这一整套存在的理由就是不那么干。
 */
export function StreamUnknown() {
  return (
    <div
      data-testid="lulu-run-stream-unknown"
      className="mt-2 flex w-full max-w-[680px] items-center gap-2 rounded-npc-card-md border border-npc-lulu-line-1 bg-npc-lulu-bg-3 px-3.5 py-2 text-npc-meta text-npc-lulu-t3"
    >
      <AlertTriangle aria-hidden className="size-3.5 shrink-0 text-npc-lulu-warn" />
      <T id="lulu_workbench.run.stream.unknown" />
    </div>
  )
}

/**
 * RunBlocks 是 view.tsx 唯一要调的东西。
 *
 * **认不出的块整块跳过**——渲染合同（blocks.go 顶部）：
 * 客户端认不出某个 type 就跳过它、显示 body。所以这里对未知 type 回 null，
 * 而 body 由调用方照常渲染，一个字都不会丢。
 */
export function RunBlocks({
  blocks,
  liveSteps,
  messageBody,
  receiptSource,
  trustedPlatformAction,
  streamDown,
  conversationId,
  decisions,
  onAnswerDecision,
  onAskLulu,
}: {
  blocks: readonly Block[] | undefined
  /**
   * 这条会话上此刻收到的**真实**过程步骤（从 SSE 的 ledger 事件攒的）。
   *
   * **可空**：没接流、或者这一屏还没连上时就是空的，那时过程卡显示
   * block 自带的那份收口摘要。两者的关系是「实时的盖住静态的」——
   * 而**都没有就什么都不画**，不补一个「正在处理」。
   */
  liveSteps?: readonly RunStep[]
  /** 这条消息的正文。传了才判得出「结果卡的结论与它逐字相同」。 */
  messageBody?: string
  /** 结果卡可信事实必须与这条消息的服务端回执来源一起判。 */
  receiptSource?: ReceiptSource
  /** 只有服务端可信位为 true，platform_action 才能展示生产发布事实。 */
  trustedPlatformAction?: boolean
  /**
   * 这条会话的事件流断了。
   *
   * **只在这一轮真有一张跑着的过程卡时才显示**那句「说不准它到哪一步了」——
   * 否则每一条安静的会话下面都会挂一句「连不上」，
   * 而那时它什么也没在做，那句话是纯噪声。
   *
   * 合同第三节：「SSE 断线**立即**显示未知/重连」。立即是对的，
   * 而「在哪儿显示」的答案是「在那张说不准的卡旁边」。
   */
  streamDown?: boolean
  /** 早晨留言那张卡上的动作要知道自己在哪条话头里；没有就只画卡，按钮按不动。 */
  conversationId?: string
  /** 待决定的红级回执；红线卡按 task_id 在里面找自己那一条。 */
  decisions?: readonly PendingDecision[] | null
  onAnswerDecision?: (decisionId: string, approve: boolean) => Promise<void>
  /** 「问 Lulu」：把那句种进输入框。 */
  onAskLulu?: (text: string) => void
}) {
  if (blocks === undefined || blocks.length === 0) return null
  // 早晨留言里每一项那张卡：与过程卡 / 结果卡不是一路，按块的顺序各自画。
  const briefCards = blocks.filter(isBriefItemCard)
  const decisionCards = blocks.filter(isTaskDecisionCard)
  // 工具自己交出来的卡（现在只有「发一张远程 AI 邀请」）。
  // **与过程卡/结果卡不是一路**：它不说这一轮干了什么，它是那件工具的产出，
  // 闲聊那一档也会有——所以它和早晨留言那张卡一样，按块的顺序各自画。
  const inviteCards = blocks.filter(isRemoteAIInviteCard)
  const extras = (
    <>
      {briefCards.map((b, i) => (
        <BriefItemCard key={`b${i}`} block={b} conversationId={conversationId} />
      ))}
      {decisionCards.map((b, i) => (
        <TaskDecisionCard
          key={`d${i}`}
          block={b}
          decisions={decisions}
          onAnswer={onAnswerDecision}
          onAsk={onAskLulu}
        />
      ))}
      {inviteCards.map((b, i) => (
        <RemoteAIInviteCard key={`i${i}`} block={b} />
      ))}
    </>
  )
  const cards = blocks.filter((b) => isProcessCard(b) || isResultCard(b))
  if (cards.length === 0) {
    if (briefCards.length === 0 && decisionCards.length === 0 && inviteCards.length === 0) return null
    return extras
  }
  // 结果卡自己不带 run_kind（服务端只在过程卡上写它），按 run_id 从同一条消息里
  // 那张过程卡上取——同一轮的两张卡一起落在同一条消息里。
  const kindByRun = new Map<string, string>()
  for (const b of cards) {
    if (!isProcessCard(b)) continue
    const raw = b as unknown as Record<string, unknown>
    const runId = str(raw, 'run_id')
    const kind = str(raw, 'run_kind')
    if (runId !== undefined && kind !== undefined) kindByRun.set(runId, kind)
  }
  // 这一轮有没有一张**还在跑**的过程卡：有结果卡就说明收口了。
  const stillRunning = cards.some(isProcessCard) && !cards.some(isResultCard)
  return (
    <>
      {extras}
      {cards.map((b, i) =>
        isProcessCard(b) ? (
          <ProcessCard key={i} block={b} liveSteps={liveSteps} />
        ) : (
          <ResultCard
            key={i}
            block={b}
            messageBody={messageBody}
            receiptSource={receiptSource}
            trustedPlatformAction={trustedPlatformAction}
            runKind={kindByRun.get(str(b as unknown as Record<string, unknown>, 'run_id') ?? '')}
          />
        ),
      )}
      {/* **断了就说断了，不留着最后那个状态。**
          留着是在用一个过期的事实冒充当下的事实，
          而这一整套存在的理由就是不那么干。 */}
      {streamDown === true && stillRunning ? <StreamUnknown /> : null}
    </>
  )
}
